import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CategoryConfigurationPage } from 'src/components/CategoryConfiguration';
import {
  EmptyState,
  Modal,
  PageHeaderWithActions,
} from 'src/components/molecules';
import {
  CategoriesAuditLogTable,
  CategoryList,
  SuggestedReading,
} from 'src/components/organisms';
import { UpdateCategoryModal } from 'src/components/organisms/UpdateCategoryModal/UpdateCategoryModal';
import { CategoryType } from 'src/graphql';
import { useCategories } from 'src/hooks/categories/useCategories';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import * as Types from 'src/types';
import { FeatureFlag, HumanReadableLanguages } from 'src/types';
import { categoryIntercomArticles } from 'src/utils/intercomArticles';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import { Loading } from '../Loading';
import { PriorityParagraph } from '../Routing/Routing.styles';
import {
  CategoriesStyled,
  CategoryListBody,
  CategoryModalList,
  CategoryModalParagraph,
  EmptyStateWrapper,
  GridContainer,
  OrderInfo,
  OrderInfoContainer,
  OrderInfoSubtitle,
  PageContainer,
} from './Categories.styles';
import { getCategoryTogglesChanged } from './helpers';

const currentLocale: keyof typeof HumanReadableLanguages = 'en-GB';

export const Categories = () => {
  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );
  const categoryConfigRedesign = useIsFeatureEnabled(
    FeatureFlag.CATEGORY_CONFIG_REDESIGN,
  );

  if (!isLegacyMode && categoryConfigRedesign) {
    return <CategoryConfigurationPage />;
  }

  return <LegacyCategories />;
};

const LegacyCategories = () => {
  const [networkError, setNetworkError] = useState<boolean>(false);
  const [openIds, setOpenIds] = useState<number[]>([]);
  const {
    currentCategory,
    setCurrentCategory,
    categories,
    coiCategories,
    isLoading,
    isCoiLoading,
    createCategory,
    newCategory,
    newCategoryRef,
    updateCategory,
    updateCategoryLocale,
    updateOrder,
  } = useCategories({ setNetworkError, setOpenIds });

  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [showConfirmChangesModal, setShowConfirmChangesModal] = useState(false);
  const [clickedCategory, setClickedCategory] = useState<CategoryType | null>(
    null,
  );
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [updateCategoryModalOpen, setUpdateCategoryModalOpen] = useState(false);

  const isCOIEnabled = useIsFeatureEnabled(FeatureFlag.CONFLICT_OF_INTEREST);
  const isAtlasThemeEnabled = useIsFeatureEnabled(FeatureFlag.ATLAS_THEME);

  const updateCategoryDetails = () => {
    updateCategory();
    setShowConfirmChangesModal(false);
    const changeCurrentCategory =
      clickedCategory && clickedCategory.id !== currentCategory?.id;
    if (changeCurrentCategory) {
      setCurrentCategory(clickedCategory);
    }
  };

  const isCurrentCategoryChanged = useMemo(() => {
    if (!currentCategory) {
      return false;
    }

    const originalCategory = [...categories, ...coiCategories].find(
      (category) => category.id === currentCategory.id,
    );

    if (!originalCategory) {
      return false;
    }

    const isChanged = getCategoryTogglesChanged(
      originalCategory,
      currentCategory,
    );
    return isChanged;
  }, [categories, coiCategories, currentCategory]);

  if (isLoading || (isCOIEnabled && isCoiLoading)) {
    return <Loading />;
  }

  const hasCategories = categories.length > 0;

  const modalCategoryNames = categories.map(
    (category) => category.locales?.[currentLocale]?.name,
  );

  const EmptyStateComponent = ({ tabIsCOI }: { tabIsCOI: boolean }) => (
    <>
      <EmptyStateWrapper>
        <EmptyState
          data-testid="category-configuration-empty-state"
          buttonText="Add new category"
          description="Customize categories and policies to highlight specific report types tailored to your company's needs. Choose 'add new category' to start. To learn more, read our guide"
          title="Welcome to Category configuration"
          intercomLink={{
            name: 'category-configuration',
            link: categoryIntercomArticles[0].link,
            title: categoryIntercomArticles[0].title,
          }}
          onButtonClick={() => createCategory({ isCOI: false })}
        />
      </EmptyStateWrapper>
      <CategoriesAuditLogTable
        isOpen={auditLogOpen}
        setIsOpen={setAuditLogOpen}
        isCOI={tabIsCOI}
      />
    </>
  );

  const CategoriesContent = ({
    categoriesData,
    tabIsCOI,
  }: {
    categoriesData: CategoryType[];
    tabIsCOI: boolean;
  }) => (
    <CategoryListBody>
      <OrderInfoContainer>
        <OrderInfoSubtitle>Order</OrderInfoSubtitle>
        <OrderInfo
          className="uil uil-info-circle"
          size="24px"
          onClick={() => setOrderModalOpen(true)}
        />
      </OrderInfoContainer>
      <GridContainer>
        <CategoryList
          setShowConfirmChangesModal={setShowConfirmChangesModal}
          isCurrentCategoryChanged={isCurrentCategoryChanged}
          categories={categoriesData}
          setCurrentCategory={setCurrentCategory}
          currentCategory={currentCategory}
          newCategory={newCategory}
          newCategoryRef={newCategoryRef}
          updateCategory={updateCategory}
          updateCategoryLocale={updateCategoryLocale}
          setClickedCategory={setClickedCategory}
          networkError={networkError}
          setNetworkError={setNetworkError}
          updateOrder={updateOrder}
          openIds={openIds}
          setOpenIds={setOpenIds}
          updateCategoryModalOpen={updateCategoryModalOpen}
          setUpdateCategoryModalOpen={setUpdateCategoryModalOpen}
          renderUpdateCategoryModal={false}
        />
        <SuggestedReading articles={categoryIntercomArticles} />
        <CategoriesAuditLogTable
          isOpen={auditLogOpen}
          setIsOpen={setAuditLogOpen}
          isCOI={tabIsCOI}
        />
      </GridContainer>
    </CategoryListBody>
  );

  const SpeakUpContent = () =>
    hasCategories ? (
      <CategoriesContent categoriesData={categories} tabIsCOI={false} />
    ) : (
      <EmptyStateComponent tabIsCOI={false} />
    );

  const pageContent = (
    <PageContainer>
      <CategoriesStyled data-testid="categories-page">
        <PageHeaderWithActions
          createItem={() => {
            if (!isCurrentCategoryChanged) {
              createCategory({ isCOI: false });
            } else {
              setShowConfirmChangesModal(true);
            }
          }}
          openAuditLog={() => setAuditLogOpen(true)}
          createButtonText="Add new category"
          title="Category configuration"
          tooltipText="We encourage you to ensure the appropriate sign offs are in place on all policy language and configuration choices."
        />

        <SpeakUpContent />

        {currentCategory && updateCategoryModalOpen && (
          <UpdateCategoryModal
            currentCategory={currentCategory}
            categoryNames={modalCategoryNames}
            updateCategoryModalOpen={updateCategoryModalOpen}
            setUpdateCategoryModalOpen={setUpdateCategoryModalOpen}
            setCurrentCategory={setCurrentCategory}
            updateCategoryLocale={updateCategoryLocale}
            networkError={networkError}
            setNetworkError={setNetworkError}
          />
        )}

        {showConfirmChangesModal && (
          <Modal
            onConfirm={updateCategoryDetails}
            onCancel={() => {
              setShowConfirmChangesModal(false);
              setCurrentCategory(clickedCategory);
            }}
            size="medium"
            title="Unsaved category details changes"
            description={
              <>
                <CategoryModalParagraph>
                  You have unsaved category detail edits. Leaving without saving
                  will result in loss of changes. Please save now or go back to
                  continue editing.
                </CategoryModalParagraph>
                <CategoryModalList>
                  <li>
                    Update details: Click &quot;Update details&quot; below.
                  </li>
                  <li>
                    Dismiss changes: Click &quot;Dismiss&quot; to abort your
                    edits.
                  </li>
                </CategoryModalList>
              </>
            }
            open={showConfirmChangesModal}
            setOpen={setShowConfirmChangesModal}
            confirmButtonText="Update details"
            cancelButtonText="Dismiss"
            data-testid={'unsaved-changes-modal'}
          />
        )}
        <Modal
          size="medium"
          title="Order categories"
          description={
            <>
              <PriorityParagraph>
                The order you set for categories will determine the order in
                which they are listed for selection by a reporter. Drag and drop
                the categories into the preferred order. All changes will be
                saved automatically.
              </PriorityParagraph>
            </>
          }
          open={orderModalOpen}
          setOpen={setOrderModalOpen}
          onConfirm={() => setOrderModalOpen(false)}
          confirmButtonText="OK"
        />
      </CategoriesStyled>
    </PageContainer>
  );

  return isAtlasThemeEnabled ? (
    <AtlasMuiThemeProvider>{pageContent}</AtlasMuiThemeProvider>
  ) : (
    pageContent
  );
};
